import clsx from 'clsx';
import { useBrand } from '@/lib/branding';

/**
 * The company's logo (t374u: per workspace, from Settings > Company & branding; see docs/branding.md).
 *
 * `surface` says what it sits on: the dark rail, sign-in backdrop, kiosk and driver header are 'dark' (the logo for
 * dark backgrounds, or the only logo); 'light' is for white pages. `compact` asks for the square mark (the collapsed
 * rail); a company without one gets its logo. A company with no logo at all gets its initials on an accent tile and
 * its name. With no branding it is The Hub's 'Modules' lockup. Shown at the `height` passed in (32px sidebar, 44px
 * sign-in, 36px kiosk); the width follows the aspect ratio.
 */
export function BrandMark({ height = 28, className, compact = false, surface = 'dark' }: { height?: number; className?: string; compact?: boolean; surface?: 'dark' | 'light' }) {
  const b = useBrand();
  const logo = surface === 'dark' ? b.logoOnDark : b.logoOnLight;
  const src = compact ? b.mark ?? logo : logo;
  if (src) return <img src={src} alt={b.title} style={{ height }} draggable={false} className={clsx('w-auto', className)} data-testid="brand-logo" />;
  const name = b.company ?? b.short;
  const initials = name.split(/\s+/).filter(w => /^[\p{L}\p{N}]/u.test(w)).slice(0, 2).map(w => w[0]!.toUpperCase()).join('');
  return (
    <span className={clsx('inline-flex items-center gap-2 select-none', className)} role="img" aria-label={b.title} style={{ height }} data-testid="brand-logo">
      <span className="flex aspect-square h-full items-center justify-center rounded-md bg-accent font-black tracking-tighter text-on-accent" style={{ fontSize: height * 0.46 }}>{initials}</span>
      {!compact && <span className={clsx('max-w-[11rem] truncate font-black leading-none tracking-tight', surface === 'dark' ? 'text-white' : 'text-ink dark:text-white')} style={{ fontSize: height * 0.5 }}>{name}</span>}
    </span>
  );
}
